// REACT
import React from "react";

// MATERIAL-UI
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";

// ASSETS
import SupportIcon from "src/icons/SupportIcon";
import { GetWalletPageBalanceApiResponse } from "src/store/features/api/generated";
import { RootState, useAppSelector } from "src/store/store";
import { formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";
import { HoldingsStyles } from "../common/styles/Holdings.styles";
import BalanceTooltip from "./BalanceTooltip";

type BalanceReturn = { available: number; total: number };

interface HoldingsProps {
  walletBalance: GetWalletPageBalanceApiResponse | undefined;
}

const availableBalanceTooltipMessage =
  "Your available balance refers to the total amount of liquid assets that you possess and can be used for trading.";

const totalTooltipMessage =
  "Total comprises all assets that are set aside for specific uses, such as target orders.";

export default function Holdings(props: HoldingsProps) {
  const { walletBalance } = props;
  const classes = HoldingsStyles();

  // redux - get users selected currency
  const { selectedCurrency } = useAppSelector((state: RootState) => state.settings);

  const balance = React.useMemo((): BalanceReturn => {
    if (!walletBalance) return { available: 0, total: 0 };

    const available = walletBalance.balance.value;
    const total = walletBalance.pending + walletBalance.balance.value;
    return { available, total };
  }, [walletBalance]);

  const HoldingsComponent = (props: { title: string; value: number; tooltipMessage: string }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    return (
      <Grid item xs={isMobile ? 12 : 6} md={6} style={{ paddingTop: "24px" }}>
        <Box className={classes.box}>
          <Grid container justifyContent="space-between">
            <Grid item>
              <Typography
                sx={{
                  inlineSize: "max-content",
                  fontSize: "14px",
                }}>
                {props.title}
              </Typography>
            </Grid>

            <Grid item>
              <BalanceTooltip message={props.tooltipMessage}>
                <SupportIcon size={14} />
              </BalanceTooltip>
            </Grid>
          </Grid>
          <Typography className={classes.title} id="wallet-balance" translate="no">
            {formatCurrency(props.value, selectedCurrency || "AUD")}
          </Typography>
        </Box>
      </Grid>
    );
  };

  return (
    <>
      {walletBalance && (
        <Grid container alignItems="center" spacing={2}>
          <HoldingsComponent
            title={"Available Balance"}
            value={balance.available}
            tooltipMessage={availableBalanceTooltipMessage}
          />
          <HoldingsComponent
            title={"Total"}
            value={balance.total}
            tooltipMessage={totalTooltipMessage}
          />
        </Grid>
      )}
    </>
  );
}
